// Copyright (c) 2021, HelmCyber, Inc.
'use strict'

import merge from 'deepmerge'
import PropTypes from 'prop-types'
import React, { useReducer } from 'react'

export const UiContext = React.createContext()

// Actions
export const RESET_ALERT_MODAL = 'RESET_ALERT_MODAL'
export const SET_ALERT_MODAL = 'SET_ALERT_MODAL'
export const SET_DATA_CAPTURED = 'SET_DATA_CAPTURED'
export const SET_NOTIFICATION_ROWS = 'SET_NOTIFICATION_ROWS'
export const SET_NOTIFICATIONS_ERROR = 'SET_NOTIFICATIONS_ERROR'
export const SET_NOTIFICATIONS_EXPANDED = 'SET_NOTIFICATIONS_EXPANDED'
export const SET_SETUP_STATE = 'SET_SETUP_STATE'
export const SET_SYSTEM_LOCK = 'SET_SYSTEM_LOCK'
export const SET_UNIT = 'SET_UNIT'
export const SET_USER = 'SET_USER'

const initialState = {
    alertModal: {
        show: false,
        title: 'Alert',
        subtitle: 'Something went wrong',
        message: 'Please contact the system administrator if the problem presists',
        children: null,
        onClose: null,
        dismissable: true,
    },
    data: {
        captured: false,
        minTidEpoch: 0,
        maxTidEpoch: 0,
        tidSize: 300000,
    },
    /*
     * `loaded` separates "the board is clear" from "we have not listed it yet",
     * which an empty `rows` cannot. The nav badge is right either way -- zero is
     * what it draws -- but a page that renders the board as a card has to say it
     * is still reading rather than showing the reader a green "nothing needs
     * attention" it will contradict a second later.
     */
    notifications: {
        rows: [],
        expanded: false,
        error: null,
        loaded: false,
    },
    /*
     * Whether this appliance has been set up yet, used to decide whether "/"
     * lands on Welcome.
     *
     * `known` starts false and stays false for roles that cannot read either
     * answer. The landing logic must treat unknown as "do not redirect": a
     * redirect decided on a default would send a configured appliance to the
     * setup page on every login.
     */
    setup: {
        known: false,
        hasEntitlements: false,
        hasFingerprints: false,
    },
    systemLock: {
        present: {
            job: null,
            locked: false,
            message: null,
        },
        pending: {
            job: null,
            locked: false,
            message: null,
        },
    },
    unit: {
        oemRelease: null,
        unitName: null,
        version: null,
    },
    user: {
        capabilities: [],
        displayName: null,
        username: null,
        uid: null,
        settings: {},
        remote: false,
    },
}

const reducer = (state = initialState, action) => {
    switch (action.type) {
        case RESET_ALERT_MODAL:
            return {
                ...state,
                alertModal: {
                    show: false,
                    title: 'Alert',
                    subtitle: 'Something went wrong',
                    message: 'Please contact the system administrator if the problem presists',
                    children: null,
                    onClose: null,
                    dismissable: true,
                },
            }
        case SET_ALERT_MODAL:
            var alertModal = Object.assign({}, state.alertModal, action.alertModal)
            return { ...state, alertModal }
        case SET_DATA_CAPTURED:
            var data = Object.assign({}, state.data, action.data)
            return { ...state, data }
        case SET_NOTIFICATION_ROWS:
            var notificationRows = { rows: Array.from(action.rows), error: null, loaded: true }
            var notifications = Object.assign({}, state.notifications, notificationRows)
            return { ...state, notifications }
        /*
         * The action was dispatched from three places and handled in none: it fell
         * through to `default` and the failure went nowhere, so the popover's own
         * error line could never render. It also has to mark the list loaded --
         * a fetch that failed has finished, and a page waiting on the answer would
         * otherwise wait for good.
         */
        case SET_NOTIFICATIONS_ERROR:
            var notificationsError = Object.assign({}, state.notifications,
                { error: action.error, loaded: true })
            return { ...state, notifications: notificationsError }
        case SET_NOTIFICATIONS_EXPANDED:
            var notificationsExpanded = Object.assign({}, state.notifications, { expanded: action.expanded })
            return { ...state, notifications: notificationsExpanded }
        case SET_SETUP_STATE:
            var setup = Object.assign({}, state.setup, action.setup)
            return { ...state, setup }
        case SET_SYSTEM_LOCK:
            var systemLock = merge(state.systemLock, action.systemLock, { clone: true })
            return { ...state, systemLock }
        case SET_UNIT:
            var unit = Object.assign({}, state.unit, action.unit)
            return { ...state, unit }
        case SET_USER:
            var user = merge(state.user, action.user, { clone: true })
            return { ...state, user }
        default:
            return state
    }
}

export const UiContextProvider = props => {
    const uiContext = useReducer(reducer, initialState)

    return <UiContext.Provider value={uiContext}>{props.children}</UiContext.Provider>
}

UiContextProvider.propTypes = {
    children: PropTypes.object,
}
